/**
 * ============================================================================
 * CENTRAL ENUM SOURCE OF TRUTH - Cloud Backend
 * ============================================================================
 * 
 * This file serves as the AUTHORITATIVE REFERENCE for all enum constants across
 * the entire project ecosystem. All other projects should copy these values.
 * 
 * Last Updated: November 7, 2025
 * Status: ✅ Complete consolidation - ALL enums from all 11 projects
 * 
 * CHANGELOG:
 * - Nov 7, 2025: Complete consolidation pass
 *                - Added 8 new enums (E-commerce, HTTP, Analytics)
 *                - Extended CABIN_STATUS with 6 additional states
 *                - Added CALL_STATION to LOCATION_TYPE
 *                - Added missing ACTION_TYPE values and aliases from NOC
 *                - Added AVAILABLE to NO_SHOW_FLAG (legacy)
 *                - Documented CABIN_STATUS value conflict between projects
 * - Nov 2, 2025: Standardized across all 11 projects
 * 
 * ⚠️ IMPORTANT - DEPLOYMENT NOTE:
 * Since projects run on separate servers/machines, they CANNOT import directly
 * from this file. Instead:
 * 
 * WORKFLOW FOR ENUM CHANGES:
 * 1. Make changes HERE FIRST (this file is the source of truth)
 * 2. Copy the updated enums to each project that needs them
 * 3. Update the comparison report (docs/ENUM_COMPARISON_REPORT.md)
 * 4. Test each project independently after enum updates
 * 
 * USAGE GUIDELINES:
 * - Define new enums here FIRST before adding to other projects
 * - Maintain consistent naming across all projects
 * - Document any project-specific variations (see Robot Screen section)
 * - Each project maintains its own copy - this is the REFERENCE version
 * 
 * SECTIONS:
 * 1. Core Robot & Order Enums (lines ~36-275)
 * 2. E-commerce Application Enums (lines ~277-397)
 * 3. Analytics Application Enums (lines ~398-419)
 * 4. Robot Screen Enums (lines ~420-540)
 * 5. Helper Functions (lines ~540+)
 * 
 * TOTAL ENUMS: 35 enum objects covering all projects
 * 
 * Using enums improves code readability and reduces magic string/number usage.
 */

// Action Status - State of robot actions
export const ACTION_STATUS = {
    PENDING: "0",           // Action created, not started
    IN_PROGRESS: "1",       // Action started
    CANCELLED: "2",         // Action cancelled/archived
    COMPLETED: "3",         // Action finished successfully
    FAILED: "4"             // Action failed/interrupted
};

/**
 * Order Status Codes - Complete order lifecycle states
 * 
 * Represents all possible states an order can be in from creation to completion.
 * These codes are synchronized across all projects for consistent order tracking.
 * 
 * STATUS FLOW:
 * 1. Creation: BOOKED (0) → CONFIRMED (1)
 * 2. Pickup: DISPATCHED (2) → APPROACHING_PICKUP (3) → ARRIVED_PICKUP (4) → PICKED_UP (5)
 * 3. Delivery: EN_ROUTE_DELIVERY (6) → APPROACHING_DELIVERY (7) → ARRIVED_DELIVERY (8) → DELIVERED (9)
 * 4. Cancellation: CANCELLED (10) or CANCELLED_ALT (15)
 * 5. Return: RETURN_INITIATED (19) → RETURNING (20) → RETURNED (21) → RETRIEVED (22)
 * 6. Partial: PARTIAL_DELIVERY (14)
 * 
 * @property {number} BOOKED - Initial state when order is placed
 * @property {number} CONFIRMED - Order confirmed by merchant/system
 * @property {number} DISPATCHED - Robot dispatched to pickup location
 * @property {number} APPROACHING_PICKUP - Robot nearing pickup location
 * @property {number} ARRIVED_PICKUP - Robot arrived at pickup point
 * @property {number} PICKED_UP - Order loaded onto robot
 * @property {number} EN_ROUTE_DELIVERY - Robot traveling to delivery location
 * @property {number} APPROACHING_DELIVERY - Robot nearing delivery location
 * @property {number} ARRIVED_DELIVERY - Robot arrived at delivery point
 * @property {number} DELIVERED - Order successfully delivered (COMPLETE)
 * @property {number} CANCELLED_BEFORE_PICKUP - Order is Cancelled before pickup
 * @property {number} CANCELLED_AFTER_PICKUP_BEFORE_DELIVERY - Order is Cancelled after pickup but before delivery
 * @property {number} CANCELLED_ALT - Alternative cancellation code - when the store/ warehouse is unable to fulfill the order
 * @property {number} RETURN_INITIATED - Return process started
 * @property {number} RETURNING - Robot returning with order
 * @property {number} RETURNED - Order returned to origin
 * @property {number} RETRIEVED - Order retrieved from robot (used by tracking-url, analytics)
 */
export const ORDER_STATUS_CODE = {
    BOOKED: 0,              // Order booked
    CONFIRMED: 1,           // Order is Confirmed
    EN_ROUTE_PICKUP: 2,     // Robot started traveling to retailer
    REACHED_PICKUP: 3,      // Robot near pickup location
    WAITING_FOR_PICKUP: 4,  // Robot waiting for pickup
    PICKED_UP: 5,           // Order has been picked up
    EN_ROUTE_DROP_OFF: 6,   // Robot started traveling to customer
    REACHED_DROP_OFF: 7,    // Robot near drop off location
    WAITING_FOR_DROP_OFF: 8,  // Robot waiting for drop off    
    DELIVERED: 9,           // Order is delivered (Complete)
    CANCELLED_BEFORE_PICKUP: 10,                        // Order is Cancelled before pickup
    CANCELLED_AFTER_PICKUP_BEFORE_DELIVERY: 14,         // Order is Cancelled after pickup but before delivery
    CANCELLED_ALT: 15,      // Alternative cancellation code - when the store/ warehouse is unable to fulfill the order
    RETURN_INITIATED: 19,   // Return process initiated
    RETURNING: 20,          // Robot returning
    RETURNED: 21,           // Order returned
    RETRIEVED: 22,          // Order retrieved from robot

    // TODO: Kept for backward compatibility, Remove after all projects are updated
    DISPATCHED: 2,
    APPROACHING_PICKUP: 3,
    ARRIVED_PICKUP: 4,
    EN_ROUTE_DELIVERY: 6,
    APPROACHING_DELIVERY: 7,
    ARRIVED_DELIVERY: 8,
    CANCELLED: 10,
    PARTIAL_DELIVERY: 14
};

// Robot Status - Overall robot availability
export const ROBOT_STATUS = {
    AVAILABLE: "AVAILABLE",
    UNAVAILABLE: "UNAVAILABLE",
    CHARGING: "CHARGING",
    ERROR: "ERROR",
    IDLE: "IDLE"
};

// Connection Status - Robot connectivity state
export const CONNECTION_STATUS = {
    ONLINE: "ONLINE",
    OFFLINE: "OFFLINE"
};

// Door Status - State of robot doors/cabins
export const DOOR_STATUS = {
    OPEN: "OPEN",
    CLOSE: "CLOSE"
};

/**
 * Cabin Status - State of robot cargo compartments
 * 
 * ⚠️ VALUE CONFLICT ALERT:
 * Different projects use different values for OCCUPIED and LOCKED:
 * - cloud-backend, robot-top-screen: OCCUPIED: "1", LOCKED: "0" (numeric strings)
 * - network-operations-console: OCCUPIED: "OCCUPIED", LOCKED: "LOCKED" (descriptive strings)
 * 
 * This enum reflects the cloud-backend/robot-top-screen values as the source of truth.
 * NOC should be updated to match these values for consistency.
 */
export const CABIN_STATUS = {
    EMPTY: "EMPTY",
    OCCUPIED: "1",              // cloud-backend/robot-top-screen value (NOC uses "OCCUPIED")
    LOCKED: "0",                // cloud-backend/robot-top-screen value (NOC uses "LOCKED")
    OPENED: "OPENED",
    OPENING: "OPENING",
    ABOUT_TO_CLOSE: "ABOUT_TO_CLOSE",
    CLOSING: "CLOSING",
    TRYING_TO_CLOSE: "TRYING_TO_CLOSE",
    TRYING_TO_OPEN: "TRYING_TO_OPEN"
};

// Robot Battery Status
export const ROBOT_BATTERY_STATUS = {
    OK: "OK",
    LOW: "LOW"
};

// Robot Running State - Physical/operational state
export const ROBOT_RUNNING_STATE = {
    ERROR: "ERROR",
    ESTOP_PRESSED: "ESTOP_PRESSED",
    NORMAL: "NORMAL",
    CHARGING: "CHARGING",
    AUTO_SYSTEM_FAULT: "AUTO_SYSTEM_FAULT",
    AUTO_OBSTACLE: "AUTO_OBSTACLE",
    AUTO_RUN_OBSTACLE: "AUTO_RUN_OBSTACLE",
    AUTO_RUN_MOVING: "AUTO_RUN_MOVING",
    AUTO_RUN: "AUTO_RUN",
    TELE_OP_OBSTACLE: "TELE_OP_OBSTACLE",
    TELE_OP_MOVING: "TELE_OP_MOVING",
    TELE_OP: "TELE_OP",
    PAYLOAD_STATE: "PAYLOAD_STATE",
    MANUAL: "MANUAL",
    COMM_WAIT_FOR_CONNECTION: "COMM_WAIT_FOR_CONNECTION"
};

// Action Type - High-level action categories
export const ACTION_TYPE = {
    MOVELOCATION: "MOVELOCATION",
    MOVELOAD: "MOVELOAD",
    MOVEUNLOAD: "MOVEUNLOAD",
    MOVEHOME: "MOVEHOME",
    MOVEWANDER: "MOVEWANDER",
    MOVESUMMON: "MOVESUMMON",
    MOVECHARGE: "MOVECHARGE",
    MOVERETRIEVE: "MOVERETRIEVE",
    CHARGING: "CHARGING",
    EXPECTLOAD: "EXPECTLOAD",
    EXPECTUNLOAD: "EXPECTUNLOAD",
    WANDER: "WANDER",
    AUTODOCK: "AUTODOCK",

    // UI-friendly action types with both string and constant versions
    // These provide aliases for consistency across projects
    Pickup: "Pickup",
    PICKUP: "Pickup",  // Alias for consistency
    Dropoff: "Dropoff",
    DROPOFF: "Dropoff",  // Alias for consistency
    "Deliver Orders": "Deliver Orders",
    DELIVER_ORDERS: "Deliver Orders",  // Alias for consistency
    "Move And Deliver": "Move And Deliver",
    MOVE_AND_DELIVER: "Move And Deliver",  // Alias for consistency
    "Move To Location": "Move To Location",
    MOVE_TO_LOCATION: "Move To Location",  // Alias for consistency
    "Move To Home": "Move To Home",
    MOVE_TO_HOME: "Move To Home",  // Alias for consistency
    Stay: "Stay",
    STAY: "Stay",  // Alias for consistency
    Wander: "Wander",
    WANDER_UI: "Wander",  // Alias for consistency

    ELEVATOR: "ELEVATOR",
    DOOR: "DOOR"
};

// Cancellable Action Types - Action types that can be cancelled via updateRobotActionStatus
// Also used to skip DB cancellation when in progress with priority > 0
export const ACTION_TYPES_CANCELLABLE_BY_ROBOT = [
    ACTION_TYPE.CHARGING
];

// Action Message - Specific action instructions
export const ACTION_MESSAGE = {
    MOVELOAD: "MOVELOAD",
    MOVEUNLOAD: "MOVEUNLOAD",
    MOVELOCATION: "MOVELOCATION",
    MOVEHOME: "MOVEHOME",
    MOVEWANDER: "MOVEWANDER",
    MOVESUMMON: "MOVESUMMON",
    MOVECHARGE: "MOVECHARGE",
    MOVERETRIEVE: "MOVERETRIEVE",
    EXPECTLOAD: "EXPECTLOAD",
    EXPECTUNLOAD: "EXPECTUNLOAD",
    EXPECTSTAY: "EXPECTSTAY",
    EXPECTRETRIEVE: "EXPECTRETRIEVE",
    CHARGING: "CHARGING",
    DELIVER_ORDERS: "Deliver Orders"
};

// Schedule Status
export const SCHEDULE_STATUS = {
    ACTIVE: "ACTIVE",
    INACTIVE: "INACTIVE"
};

// Preparation Status Code
export const PREPARATION_STATUS_CODE = {
    NOT_READY: 0,
    READY: 1
};

// No Show Flag - Customer pickup status
export const NO_SHOW_FLAG = {
    NO_SHOW_NONE: 0,
    NO_SHOW_FLAGGED: 1,
    NO_SHOW_CONFIRMED: 2,
    AVAILABLE: "AVAILABLE"  // Legacy: Used by NOC for backward compatibility
};

// Order Type
export const ORDER_TYPE = {
    TEST: "TEST",
    LIVE: "LIVE",
    DUMMY: "DUMMY"
};

// Language Options
export const LANGUAGE = {
    ENGLISH: "English",
    SPANISH: "Spanish",
    ITALIAN: "Italian"
};

// Mission Type
export const MISSION_TYPE = {
    SEQUENTIAL: "sequential",
    SCHEDULED: "scheduled",
    MANUAL: "manual"
};

// Recurrence Type
export const RECURRENCE_TYPE = {
    DAILY: "DAILY",
    WEEKDAYS: "WEEKDAYS",
    UNTIL_STOPPED: "UNTIL STOPPED"
};

// PIN Type
export const PIN_TYPE = {
    PHONE: "PHONE",
    ORDERID: "ORDERID",
    RANDOM: "RANDOM"
};

// Scheduler Method
export const SCHEDULER_METHOD = {
    POST: "post",
    GET: "get"
};

// Idle Location
export const IDLE_LOCATION = {
    PICKUP: "PICKUP",
    HOME: "HOME"
};

// Cabin Position
export const CABIN_POSITION = {
    LEFT: "l",
    RIGHT: "r"
};

// Job State
export const JOB_STATE = {
    WAITING: "waiting",
    ACTIVE: "active",
    DELAYED: "delayed",
    PAUSED: "paused",
    PRIORITIZED: "prioritized",
    WAITING_CHILDREN: "waiting-children"
};

// Location Type
export const LOCATION_TYPE = {
    HOME: "HOME",
    PICKUP: "PICKUP",
    DROPOFF: "DROPOFF",
    CHARGER: "CHARGER",
    RELOCALIZATION: "RELOCALIZATION",
    CHARGING: "CHARGING",
    WANDER: "WANDER",
    CALL_STATION: "CALL-STATION"
};

// ============================================================================
// E-COMMERCE APPLICATION ENUMS
// ============================================================================

/**
 * Store Status - Store availability state
 * Used by: ecommerce-app-backend, ecommerce-app-consumer-frontend, ecommerce-app-store-frontend
 * 
 * @property {number} CLOSED - Store is closed/unavailable
 * @property {number} OPEN - Store is open and accepting orders
 */
export const STORE_STATUS = {
    CLOSED: 0,
    OPEN: 1
};

/**
 * HTTP Response Code - API response status codes
 * Used by: ecommerce-app-backend
 * 
 * Custom HTTP-like status codes used for API responses in e-commerce system.
 * 
 * @property {number} SUCCESS - Request successful
 * @property {number} DATA_MISSING - Required data missing in request
 * @property {number} ERROR - General error
 * @property {number} RESULT_NOT_FOUND - Requested resource not found
 * @property {number} ALREADY_EXISTS - Resource already exists
 * @property {number} INVALID_REQUEST - Invalid request format
 * @property {number} TOKEN_EXPIRE - Authentication token expired
 */
export const HTTP_RESPONSE_CODE = {
    SUCCESS: 200,
    DATA_MISSING: 400,
    ERROR: 402,
    RESULT_NOT_FOUND: 404,
    ALREADY_EXISTS: 409,
    INVALID_REQUEST: 501,
    TOKEN_EXPIRE: 503
};

/**
 * HTTP Status - Standard HTTP status codes
 * Used by: ecommerce-app-consumer-frontend, tracking-url
 * 
 * Simplified set of standard HTTP status codes used by frontend applications.
 * 
 * @property {number} OK - Request successful (200)
 * @property {number} BAD_REQUEST - Bad request/malformed syntax (400)
 */
export const HTTP_STATUS = {
    OK: 200,
    BAD_REQUEST: 400
};

/**
 * Robot Spawn Status - Robot spawning state
 * Used by: ecommerce-app-backend, ecommerce-app-store-frontend
 * 
 * @property {number} SPAWNING - Attempting to spawn robot
 * @property {number} SPAWNED - Robot successfully spawned
 * @property {number} SPAWN_FAILED - Robot spawn failed
 */
export const ROBOT_SPAWN_STATUS = {
    SPAWNING: 0,
    SPAWNED: 1,
    SPAWN_FAILED: 2
};

/**
 * Payment Status - Payment transaction states
 * Used by: ecommerce-app-backend, ecommerce-app-consumer-frontend
 * 
 * @property {string} SUCCEEDED - Payment completed successfully
 */
export const PAYMENT_STATUS = {
    SUCCEEDED: "succeeded"
};

/**
 * API Status - External service response codes
 * Used by: ecommerce-app-backend
 * 
 * Status codes for external API integrations and services.
 * 
 * @property {string} SUCCESS - HTTP 200 as string
 * @property {string} A200 - Service-specific success code
 * @property {string} A001 - Service-specific code
 */
export const API_STATUS = {
    SUCCESS: "200",
    A200: "A200",
    A001: "A001",
    E501: "E501"  // Undelivered orders exist - cannot start mission
};

/**
 * HMS Status Code - HMS API response status codes
 * Used by: ecommerce-app-backend
 * 
 * Status codes used for HMS (Hotel Management System) API integration.
 * These are string representations of status codes specific to HMS.
 * 
 * @property {string} DISPATCHED - Order dispatched/confirmed
 * @property {string} CANCELLED - Order cancelled
 */
export const HMS_STATUS_CODE = {
    DISPATCHED: "2",
    CANCELLED: "11"
};

/**
 * Product Status - Product/category active state
 * Used by: ecommerce-app-backend, ecommerce-app-consumer-frontend, ecommerce-app-store-frontend
 * 
 * @property {number} INACTIVE - Product/category is inactive/disabled
 * @property {number} ACTIVE - Product/category is active/enabled
 */
export const PRODUCT_STATUS = {
    INACTIVE: 0,
    ACTIVE: 1
};

// ============================================================================
// ANALYTICS APPLICATION ENUMS
// ============================================================================

/**
 * Granularity Options - Time aggregation levels
 * Used by: delivery-analytics-frontend
 * 
 * Defines time aggregation levels for analytics data visualization and reporting.
 * 
 * @property {string} HOURS - Hourly aggregation
 * @property {string} DAYS - Daily aggregation
 * @property {string} WEEKS - Weekly aggregation
 * @property {string} MONTHS - Monthly aggregation
 */
export const GRANULARITY_OPTIONS = {
    HOURS: "hours",
    DAYS: "days",
    WEEKS: "weeks",
    MONTHS: "months"
};

// ============================================================================
// ROBOT SCREEN ENUMS
// ============================================================================

/**
 * Robot Current Mode - Defines the operational mode of the robot
 * Used by: robot-top-screen
 * 
 * @property {string} MAPPING - Robot is in mapping mode, creating/updating the environment map
 * @property {string} AUTONOMOUS - Robot is operating autonomously following planned routes
 * @property {string} IDLE - Robot is idle, not performing any tasks
 * @property {string} FOLLOWME - Robot is in follow-me mode, tracking a person/device
 * @property {string} TELEOP - Robot is under remote teleoperation control
 */
export const ROBOT_CURRENT_MODE = {
    MAPPING: "mapping",
    AUTONOMOUS: "auto",
    IDLE: "idle",
    FOLLOWME: "followme",
    TELEOP: "teleop"
};

/**
 * Charging Status - Indicates whether the robot is currently charging
 * Used by: robot-front-screen
 * 
 * @property {string} CHARGING - Robot is connected to charger and actively charging
 * @property {string} NOT_CHARGING - Robot is not charging (may be on battery or disconnected)
 */
export const CHARGING_STATUS = {
    CHARGING: "CHARGING",
    NOT_CHARGING: "NOT_CHARGING"
};

/**
 * Parcel Type - Flags to indicate if robot is carrying a parcel
 * Used by: robot-front-screen
 * Note: String boolean values used for ROS compatibility
 * 
 * @property {string} PARCEL - Robot is carrying a parcel (true as string)
 * @property {string} NON_PARCEL - Robot is not carrying a parcel (false as string)
 */
export const PARCEL_TYPE = {
    PARCEL: "true",
    NON_PARCEL: "false"
};

/**
 * Docking States - Array of allowed state IDs when robot can initiate docking
 * Used by: robot-top-screen
 * Purpose: Prevents docking attempts during incompatible robot states
 * 
 * Allowed states: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 12, 13, 14, 16]
 * These correspond to specific robot operational states where docking is safe
 */
export const DOCKING_STATES = {
    ALLOWED: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 12, 13, 14, 16]
};

/**
 * Elevator States - Top Screen Version
 * Used by: robot-top-screen
 * 
 * Array of allowed elevator state IDs when robot can interact with elevator system.
 * This is the COMPREHENSIVE version with 13 states for full elevator interaction.
 * 
 * Allowed states: [0, 1, 5, 7, 8, 9, 10, 11, 15, 16, 19, 20, 25]
 * 
 * DIFFERENCE FROM FRONT SCREEN: Top screen includes additional states (7, 8, 10, 16, 19, 20)
 * for more granular elevator control and status display.
 * 
 * See ELEVATOR_STATES_COMPARISON below for detailed difference documentation.
 */
export const ELEVATOR_STATES_TOP_SCREEN = {
    ALLOWED: [0, 1, 5, 7, 8, 9, 10, 11, 15, 16, 19, 20, 25, 26, 27]
};

/**
 * Elevator States - Front Screen Version
 * Used by: robot-front-screen
 * 
 * Array of allowed elevator state IDs for basic elevator interaction.
 * This is the SIMPLIFIED version with 7 states for essential elevator operations.
 * 
 * Allowed states: [0, 1, 5, 9, 11, 15, 25]
 * 
 * DIFFERENCE FROM TOP SCREEN: Front screen uses a minimal subset of states,
 * likely because it only needs to handle essential elevator transitions
 * (entering, navigating, exiting) rather than monitoring all intermediate states.
 * 
 * See ELEVATOR_STATES_COMPARISON below for detailed difference documentation.
 */
export const ELEVATOR_STATES_FRONT_SCREEN = {
    ALLOWED: [0, 1, 5, 9, 11, 15, 25, 26]
};

/**
 * ELEVATOR_STATES_COMPARISON - Documentation of Differences
 * ===========================================================
 * 
 * WHY TWO VERSIONS EXIST:
 * - Top Screen: Monitoring/control interface requiring full state visibility
 * - Front Screen: Customer-facing display requiring only essential state changes
 * 
 * COMMON STATES (in both versions):
 *   0, 1, 5, 9, 11, 15, 25
 * 
 * TOP SCREEN ONLY (6 additional states):
 *   7, 8, 10, 16, 19, 20
 *   These likely represent intermediate elevator states, door operations,
 *   or error/recovery states not relevant to customer display.
 * 
 * RECOMMENDATION:
 * - Keep both versions as they serve different purposes
 * - Top screen needs comprehensive state awareness for operations
 * - Front screen needs minimal states for customer experience
 * - Document any state ID meanings in robot firmware documentation
 */

/**
 * Elevator State Values - Specific named elevator states
 * Used by: robot-front-screen
 * 
 * Maps human-readable names to elevator state IDs for common operations.
 * These are the most critical states for elevator interaction.
 * 
 * @property {number} ENTERING - Robot is entering the elevator (state 9)
 * @property {number} EXITING - Robot is exiting the elevator (state 15)
 * @property {number} NAVIGATING - Robot is navigating inside elevator (state 11)
 */
export const ELEVATOR_STATE = {
    ENTERING: 9,
    EXITING: 15,
    NAVIGATING: 11,
    ALIGNING_TOWARDS_ELEVATOR: 26,
    FAILED_TO_ALIGNING_TOWARDS_ELEVATOR: 27
};

// ============================================================================
// ANALYTICS & REPORTING ENUMS
// ============================================================================

/**
 * Distance Unit - Unit of measurement for distance/metrics
 * Used by: network-operations-console, analytics systems
 * 
 * Standardizes distance unit values used in reporting and analytics.
 * 
 * @property {string} KILOMETERS - Kilometers (metric)
 * @property {string} MILES - Miles (imperial)
 * @property {string} METERS - Meters (metric, fine-grained)
 */
export const DISTANCE_UNIT = {
    KILOMETERS: "KM",
    MILES: "miles",
    METERS: "meters"
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

// Helper function to get all values from an enum
export const getEnumValues = (enumObj) => Object.values(enumObj);

// Helper function to check if a value is valid for an enum
export const isValidEnumValue = (enumObj, value) => Object.values(enumObj).includes(value);

